import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGetQR } from "hooks/useGetQR";
import { useIsMobile } from "hooks/useIsMobile";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

const StyledDialogTitle = styled(DialogTitle)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const StyledTitleText = styled(Box)(() => ({
  flex: 1,
}));

const StyledDescriptionText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledQrCenterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 260,
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const StyledQrCodeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-container": {
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
    },
  },
  "& .MuiDialog-paper": {
    maxWidth: 444,
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      transform: "translateX(-16em)",
      maxWidth: "calc(100vw - 16px)",
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

type Props = {
  open: boolean;
  onClose: () => void;
};

export const QrCodeLoginModal: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    data: qrData,
    loading,
    error,
    refetch,
  } = useGetQR({ enabled: false });

  const qrSize = isMobile ? 280 : 340;
  const confirmationNumberSize = isMobile ? 56 : 70;

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open, refetch]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <StyledDialogTitle>
        <StyledTitleText>{t("qrModal.loginToQr")}</StyledTitleText>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent dividers={isMobile}>
        <StyledDescriptionText variant="body2">
          {t("qrModal.scanQrCodeToLogin")}
        </StyledDescriptionText>

        <StyledQrCenterContainer>
          {loading || error ? (
            <CircularProgress />
          ) : (
            <StyledQrCodeContainer>
              <QRCode value={qrData?.qr ?? ""} size={qrSize} />
              <Typography
                variant="body2"
                align="center"
                fontSize={confirmationNumberSize}
              >
                {qrData?.confirmationNumber}
              </Typography>
            </StyledQrCodeContainer>
          )}
        </StyledQrCenterContainer>
      </DialogContent>

      <StyledDialogActions>
        <Button onClick={onClose} color="error" variant="outlined">
          {t("common.close")}
        </Button>
        <LoadingButton
          loading={loading}
          onClick={() => refetch()}
          variant="contained"
        >
          {t("common.refresh")}
        </LoadingButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};